import React, { useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  alpha,
  useTheme,
  Badge,
  Divider,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Build as BuildIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

/**
 * Panel to display all damaged items across all OUTPUT line items
 * Allows creating reconstruction batches
 *
 * Props:
 * - damagedBatches: Array of check records with damaged items
 * - onCreateReconstruction: function(checkRecord) - callback to create reconstruction
 * - onViewReconstructions: function(checkId) - callback to view reconstruction history
 */
function DamagedItemsPanel({
  damagedBatches,
  onCreateReconstruction,
  onViewReconstructions,
}) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  if (!damagedBatches || damagedBatches.length === 0) {
    return null; // Don't show panel if no damaged items
  }

  const getNetDamagedQty = (batch) => {
    const recovered = (batch.reconstructions || [])
      .filter((r) => r.status === "COMPLETED")
      .reduce((sum, r) => sum + (r.reconstructedQty || 0), 0);
    return Math.max(0, (batch.damagedQty || 0) - recovered);
  };

  const visibleBatches = damagedBatches.filter((b) => getNetDamagedQty(b) > 0);

  const totalDamaged = visibleBatches.reduce((sum, batch) => {
    return sum + getNetDamagedQty(batch);
  }, 0);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Card
      elevation={1}
      sx={{
        mb: 4,
        borderRadius: 2,
        border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
      }}
    >
      <CardContent>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon sx={{ color: "error.main" }} />
            <Typography variant="subtitle1" fontWeight="700" color="error.main">
              Damaged Items
            </Typography>
            <Badge badgeContent={visibleBatches.length} color="error">
              <Chip
                label={`${totalDamaged.toFixed(0)} total damaged`}
                size="small"
                color="error"
                sx={{ ml: 1 }}
              />
            </Badge>
          </Box>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Expandable Content */}
        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary" paragraph>
            These items failed quality control and can be
            reconstructed/repaired. Click "Reconstruct" to start the repair
            process.
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}
                >
                  <TableCell>Check ID</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Batch Received</TableCell>
                  <TableCell align="right">Damaged Qty</TableCell>
                  <TableCell>Checked By</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleBatches.map((batch) => (
                  <TableRow
                    key={batch.id}
                    sx={{
                      "&:hover": {
                        bgcolor: alpha(theme.palette.error.main, 0.02),
                      },
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={`#${batch.id}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {batch.receive?.subOrderLineItem?.imports?.itemName ||
                          "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {batch.receive?.subOrderLineItem?.imports?.brand}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        Batch #{batch.receive?.id}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        color="error.main"
                      >
                        {getNetDamagedQty(batch).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {batch.checkedBy}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(batch.checkedDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          maxWidth: 150,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                        }}
                      >
                        {batch.notes || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip title="Create Reconstruction">
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<BuildIcon />}
                            onClick={() => onCreateReconstruction(batch)}
                          >
                            Reconstruct
                          </Button>
                        </Tooltip>
                        <Tooltip title="View Reconstructions">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => onViewReconstructions(batch.id)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default DamagedItemsPanel;
